import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { ChoroplethScale, CrmMapMetrik, METRIK_LABELS } from '../../../services/crmMapData';

// Legende der Flaechen-Einfaerbung plus die Attributionszeile.
//
// Die Attribution ist NICHT optional und deshalb auch nicht ausblendbar:
// public/geodata/README.md verlangt „© GeoBasis-DE / BKG 2019" (dl-de/by-2.0
// fuer die Verwaltungsgrenzen) und „© OpenStreetMap contributors" (ODbL fuer
// die PLZ-Zuordnung und die Kartenkacheln). Sie steht bewusst in derselben
// Komponente wie die Legende — so kann niemand die Legende entfernen und die
// Namensnennung dabei versehentlich mitnehmen.

interface Props {
    scale: ChoroplethScale;
    metrik: CrmMapMetrik;
    /** true, solange die Aggregat-RPCs fehlen (Migration 17 nicht gefahren). */
    aggregateFehlen?: boolean;
    /** Wird das Choropleth ueberhaupt gezeigt? */
    sichtbar: boolean;
}

const ATTRIBUTION_ZEILEN = [
    '© GeoBasis-DE / BKG 2019 (dl-de/by-2.0)',
    '© OpenStreetMap contributors',
    'Kreis-/Landzuordnung: suche-postleitzahl.org, Datenbasis BKG',
];

export const CrmMapLegend: React.FC<Props> = ({ scale, metrik, aggregateFehlen, sichtbar }) => (
    <div className="glass rounded-2xl px-4 py-3 max-w-[280px] pointer-events-auto">
        {sichtbar && (
            <>
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                    {METRIK_LABELS[metrik]} je Region
                </p>

                {aggregateFehlen ? (
                    <p className="text-[11px] text-amber-700 leading-snug flex gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                        <span>
                            Aggregate nicht verfügbar — <code className="font-mono">crm_map_aggregates.sql</code>{' '}
                            (Migration 17) ist auf dieser Umgebung noch nicht gefahren. Die Marker-Ebenen
                            funktionieren davon unabhängig.
                        </span>
                    </p>
                ) : scale.klassen.length === 0 ? (
                    <p className="text-[11px] text-slate-500">Keine Werte im gewählten Zeitraum.</p>
                ) : (
                    <ul className="space-y-1">
                        {scale.klassen.map((k) => (
                            <li key={k.index} className="flex items-center gap-2 text-[11px] text-slate-600">
                                <span
                                    className="w-4 h-3 rounded-sm border border-slate-300/70 shrink-0"
                                    style={{ backgroundColor: k.farbe }}
                                />
                                <span className="tabular-nums">{k.label}</span>
                                <span className="ml-auto text-slate-400 tabular-nums">{k.anzahl}</span>
                            </li>
                        ))}
                        <li className="flex items-center gap-2 text-[11px] text-slate-500 pt-1">
                            <span
                                className="w-4 h-3 rounded-sm border border-slate-300/70 shrink-0"
                                style={{ backgroundColor: scale.farbeUnbekannt }}
                            />
                            <span>ohne Wert</span>
                        </li>
                    </ul>
                )}

                {scale.geringeDatenbasis && (
                    <p className="mt-2 text-[11px] text-amber-700 leading-snug">
                        Geringe Datenbasis: weniger als 5 Regionen mit Werten — die Klassengrenzen sagen
                        entsprechend wenig aus.
                    </p>
                )}
            </>
        )}

        <p className="mt-3 pt-2 border-t border-slate-200/70 text-[10px] leading-relaxed text-slate-400">
            {ATTRIBUTION_ZEILEN.map((z) => (
                <span key={z} className="block">
                    {z}
                </span>
            ))}
        </p>
    </div>
);
